import { Icon } from '@iconify/react';
import { useWorkspaceInfo } from '@modules/workspace/providers/WorkspaceInfo';
import {
  Grid,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { IWorkspace } from 'src/@types/workspace';
import { routes } from 'src/Router/paths';
import { useAuthContext } from 'src/auth/useAuthContext';
import { CustomAvatar } from 'src/components/custom-avatar';
import Iconify from 'src/components/iconify';
import { useWorkspaceInviteUserDialog } from 'src/features/workspace/WorkspaceInviteUserDialog';
import { useWorkspaceMembersDialog } from 'src/features/workspace/workspaceMembersDialog';
import { useSubscription } from 'src/hooks/useSubscription';
import { useWorkspaceRealMembers } from 'src/hooks/useWorkspace';
import { useLocales } from 'src/locales';
import { useDispatch } from 'src/redux/store';
import { appLocationState, startSessionFromLogin } from 'src/state';
import { axios } from 'src/utils/axios';
import { openAppAsUser } from 'src/utils/user-utils';

type DropdownOptions = {
  label: string;
  icon: React.ReactElement;
  onClick?: VoidFunction;
  component?: JSX.Element;
  disabled?: boolean;
};

export function WorkspaceDropdown() {
  const theme = useTheme();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { translate } = useLocales();

  const { workspaceLocked } = useSubscription();

  const { workspace } = useWorkspaceInfo();
  const { showWorkspaceMembersDialog } = useWorkspaceMembersDialog();
  const { showWorkspaceInviteUserDialog } = useWorkspaceInviteUserDialog();

  const members = useWorkspaceRealMembers();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { subscription } = useSubscription();

  const { user } = useAuthContext();

  const workspaces = useMemo(
    () =>
      user?.myUsers.filter((item) => item.workspace._id !== workspace._id) ||
      [],
    [user, workspace],
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const OPTIONS: DropdownOptions[] = [
    {
      label: translate('header.workspace_dropdown.invite_item'),
      icon: <Icon icon="tabler:user-plus" width={24} />,
      onClick: () => showWorkspaceInviteUserDialog(),
      disabled: workspaceLocked,
    },
    {
      label: translate('header.workspace_dropdown.view_members_item'),
      icon: <Icon icon="tabler:users" width={24} />,
      onClick: () => showWorkspaceMembersDialog(),
      disabled: workspaceLocked,
    },
    {
      label: translate('header.workspace_dropdown.teams_item'),
      icon: <Icon icon="tabler:users-group" width={24} />,
      onClick: () => navigate(routes.settings.workspace.teams),
      disabled: workspaceLocked,
    },
    // {
    //   label: translate('header.workspace_dropdown.notification_item'),
    //   icon: <Icon icon="tabler:bell-ringing" width={24} />,
    //   disabled: workspaceLocked,
    // },
    {
      label: translate('header.workspace_dropdown.billing_item'),
      icon: <Icon icon="tabler:credit-card" width={24} />,
      onClick: () => navigate(routes.settings.workspace.billing),
    },
    {
      label: translate('header.workspace_dropdown.workspace_settings_item'),
      icon: <Icon icon="tabler:settings" width={24} />,
      onClick: () => navigate(routes.settings.workspace.general),
      disabled: workspaceLocked,
    },
    // {
    //   label: translate("header.workspace_dropdown.add_workspace_item"),
    //   icon: <Icon icon="akar-icons:grid" width={24} />,
    //   onClick: () => navigate(routes.wizard.createWorkspace)
    // },
    // {
    //   label: translate("header.workspace_dropdown.leave_workspace_item"),
    //   icon: <Icon icon="tabler:login" width={24} />,
    //   onClick: () => alert("Coming soon!")
    // }
  ];

  const handleClickAccount = (userId: string, replace = true) => {
    handleClose();
    openAppAsUser(userId, replace);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!anchorEl) return;
    const { locations, masterToken } = appLocationState.getState();

    const totalWorkspaces = workspaces.length + 1; // Sum 1 because workspaces is a memo filtering current/active workspace.
    const totalAccessTokens = Object.keys(locations).length;

    if (totalWorkspaces !== totalAccessTokens) {
      axios
        .post(
          '/system/user/access-token/refresh',
          {},
          {
            headers: {
              authorization: masterToken,
            },
          },
        )
        .then((d) => {
          startSessionFromLogin(d.data);
        });
    }
  }, [anchorEl]);

  const WorkspaceLayout = ({
    workspace,
    userId,
    plan,
    users,
  }: {
    workspace: IWorkspace;
    userId?: string;
    plan?: string;
    users?: number;
  }) => (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      px={2}
      py={1}
      width={1}
    >
      <CustomAvatar
        src={workspace.picture}
        alt={workspace.name}
        name={workspace.name}
        variant="rounded"
        sx={{ height: '32px', width: '32px' }}
      />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width={1}
        gap={1.5}
      >
        <Stack>
          <Typography variant="subtitle2" whiteSpace="nowrap">
            {workspace.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          >
            {plan
              ? `${translate('header.workspace_dropdown.plan', { plan })}`
              : ''}
            {users
              ? ` ${translate('header.workspace_dropdown.user_count', { users })}`
              : ''}
          </Typography>
        </Stack>
        {userId && (
          <Tooltip
            title={translate(
              'header.workspace_dropdown.workspace_in_new_tab_text',
            )}
          >
            <StyledExternalIcon
              icon="tabler:external-link"
              onClick={(e) => {
                e.stopPropagation();
                handleClickAccount(userId, false);
              }}
            />
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );

  return (
    <Grid>
      <Grid
        sx={{
          alignItems: 'center',
          display: 'flex',
          padding: theme.spacing(0.75, 1),
          cursor: 'pointer',
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          color: '#fff',
        }}
        onClick={handleClick}
      >
        <CustomAvatar
          src={workspace.picture}
          alt={workspace.name}
          name={workspace.name}
          variant="rounded"
          sx={{
            height: '24px',
            width: '24px',
            fontSize: '1rem',
            marginRight: 1,
          }}
        />

        <ListItemText
          primary={workspace.name}
          primaryTypographyProps={{ typography: 'body2' }}
          secondaryTypographyProps={{ typography: 'caption' }}
          sx={{
            display: {
              xs: 'none',
              lg: 'flex',
            },
            marginRight: 1,
            whiteSpace: 'nowrap',
          }}
        />
        {anchorEl === null ? (
          <Iconify icon="icon-park-outline:down" />
        ) : (
          <Iconify icon="icon-park-outline:up" />
        )}
      </Grid>

      <Menu
        id="header-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {WorkspaceLayout({
          workspace,
          plan: subscription?.plan?.name,
          users: members.length,
        })}
        <Stack
          gap={1}
          borderTop="1px solid"
          borderBottom="1px solid"
          borderColor="divider"
          py={1}
        >
          {OPTIONS.map((option, index) => (
            <MenuItem
              onClick={() => {
                option.onClick?.();
                handleClose();
              }}
              disableRipple
              key={`workspace-dropdown-${index}`}
              disabled={option.disabled != null ? option.disabled : false}
              sx={{ py: 1 }}
            >
              {option.icon}
              <ListItemText
                sx={{ marginLeft: '8px' }}
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{ typography: 'caption' }}
              >
                {option.label}
              </ListItemText>
            </MenuItem>
          ))}
        </Stack>
        <Stack gap={1}>
          <Typography
            variant="button"
            sx={{ px: 2, pt: 1, color: theme.palette.text.secondary }}
          >
            {translate('header.workspace_dropdown.switch_workspaces')}
          </Typography>

          {workspaces.map((myUser) => (
            <MenuItem
              onClick={() => handleClickAccount(myUser._id)}
              disableRipple
              sx={{ padding: 0 }}
              key={myUser._id}
            >
              {WorkspaceLayout({
                workspace: myUser.workspace,
                userId: myUser._id,
              })}
            </MenuItem>
          ))}

          <MenuItem
            onClick={() => {
              navigate(routes.wizard.createWorkspace);
              handleClose();
            }}
            disableRipple
            sx={{ py: 1 }}
          >
            <Icon icon="tabler:plus" width={24} />
            <ListItemText
              sx={{ marginLeft: '8px' }}
              primaryTypographyProps={{ typography: 'body2' }}
              secondaryTypographyProps={{ typography: 'caption' }}
            >
              {translate('header.workspace_dropdown.add_workspace_item')}
            </ListItemText>
          </MenuItem>
        </Stack>
      </Menu>
    </Grid>
  );
}

const StyledExternalIcon = styled(Iconify)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.text.primary,
  },
}));
